/**
 * Everywhere user-authored rich text is rendered: `user-edited-content` on the
 * server-rendered pages, `prose` in the knowledge base article component.
 */
const codeBlockSelector = '.user-edited-content pre > code, .prose pre > code'

/** highlight.js and its stylesheets, fetched once and only when needed. */
async function loadHighlighter() {
  return (await import('./highlighter')).default
}

/**
 * Highlights code blocks that have not been highlighted yet, loading
 * highlight.js only when there are any.
 *
 * Safe to call more than once: content rendered by Vue arrives after the
 * initial pass, and highlight.js warns when an element is highlighted twice.
 * The check is repeated after loading, since another call may have highlighted
 * the same block in the meantime.
 */
export async function highlightCodeBlocks(
  root: ParentNode = document,
): Promise<void> {
  const isUnhighlighted = (block: HTMLElement): boolean =>
    block.dataset.highlighted !== 'yes'

  if (
    !Array.from(root.querySelectorAll<HTMLElement>(codeBlockSelector)).some(
      isUnhighlighted,
    )
  ) {
    return
  }

  const hljs = await loadHighlighter()

  root
    .querySelectorAll<HTMLElement>(codeBlockSelector)
    .forEach(function (block) {
      if (isUnhighlighted(block)) {
        hljs.highlightElement(block)
      }
    })
}

/** Highlights a single element outside the rich-text containers. */
export async function highlightElement(element: HTMLElement): Promise<void> {
  const hljs = await loadHighlighter()

  hljs.highlightElement(element)
}

/** Runs the first pass as soon as the server-rendered markup is available. */
export function highlightCodeBlocksWhenReady(): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      void highlightCodeBlocks()
    })

    return
  }

  void highlightCodeBlocks()
}
